import React, { useEffect, useMemo } from "react";

import { useNavigate } from "react-router";

import { RequestProposalCard } from "@companion-core/shared/app/Components/Requests/RequestProposalCard";
import { RequestRecommendationsProps } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestRecommendations/requestRecommendations.model";
import { useRequestRecommendations } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestRecommendations/hooks/useRequestRecommendations";
import { sanitizeHtml } from "@companion-core/shared/app/Utils/htmlSanitizer";
import { trackClickRequestRecommendation } from "@companion-core/shared/app/tracking/requests";
import { getFamilyIcon } from "@companion-core/shared/app/Utils/myRequests";

import "@companion-core/web/src/Assets/Styles/scss/myRequestsRecommendations.scss";

export const RequestRecommendations = ({
  requestData,
  proposalsData,
}: RequestRecommendationsProps) => {
  const navigate = useNavigate();
  const {
    proposalId,
    recommendations,
    introduction,
    conclusion,
    getRecommendationMedia,
    getRecommendationMediaAlt,
    getRecommendationSubtitle,
    getRecommendationBudget,
  } = useRequestRecommendations({ requestData, proposalsData });
  const hasDigitalProposal = useMemo(() => requestData.has_digital_proposal, [requestData]);

  useEffect(() => {
    if (!hasDigitalProposal) navigate("/my-requests");
  }, [hasDigitalProposal]);

  return (
    <div data-testid="request-detail__recommendations" className="request-detail__recommendations">
      {introduction && (
        <p
          data-testid="request-detail__recommendations--introduction"
          className="body-text-6"
          // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(introduction) }}
        />
      )}
      <div
        data-testid="request-detail__recommendations--list"
        className="request-detail__recommendations-list">
        {recommendations.map((recommendation, index) => (
          <RequestProposalCard
            key={recommendation.id}
            id={recommendation.id}
            href={`/my-requests/${requestData.id}/proposals/${proposalId}/recommendations/${recommendation.id}`}
            tracked
            position={index}
            nbItems={recommendations.length}
            url={window.location.href}
            testID="request-detail__recommendations--card"
            media={getRecommendationMedia(recommendation)}
            mediaAlt={getRecommendationMediaAlt(recommendation)}
            iconOverlay={
              !getRecommendationMedia(recommendation)
                ? getFamilyIcon(requestData.family ?? "", requestData.sub_family)
                : undefined
            }
            title={recommendation.title}
            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
            subtitle={sanitizeHtml(getRecommendationSubtitle(recommendation))}
            budget={getRecommendationBudget(recommendation)}
            vendorId={recommendation.vendor_id ?? undefined}
            onClick={() => {
              trackClickRequestRecommendation({
                family: requestData.family,
                subFamily: requestData.sub_family,
                requestId: requestData.id,
                vendorId: recommendation.vendor_id,
                status: recommendation.status,
              });
            }}
          />
        ))}
      </div>
      {conclusion && (
        <p
          data-testid="request-detail__recommendations--conclusion"
          className="request-detail__recommendations-conclusion body-text-6"
          // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(conclusion) }}
        />
      )}
    </div>
  );
};
